import { PAYMENT_TYPE } from '@mkt-client/subscription';
import {
	AOFL_PRODUCT_UUIDS,
	PLATFORMS,
	LOCAL_STORAGE_KEYS,
	COOKIES,
	PAGES
} from '$lib/utils/constants';
import { ProductExperienceService } from '$lib/services/product-experience';
import { getDeviceId, saveDeviceId } from '$lib/utils/device-id';
import { browser } from '$app/environment';
import { get } from 'svelte/store';
import { user } from '$lib/stores/user/store';
import { app } from '$lib/stores/app/store';
import { Sentry } from '$lib/services/sentry';
import APIError from './error';
import { CaptchaService } from '$lib/services/captcha';
import { validate as validateUuid } from 'uuid';

/** @type {Function} */
let fetchFunc;
/**
 * @type {new (...args: any[]) => Headers} HeadersConstructor
 */
let HeadersFunc;
/**
 * @type {new (...args: any[]) => FormData} HeadersConstructor
 */
let FormDataFunc;

(async function () {
	if (browser) {
		fetchFunc = window.fetch;
		HeadersFunc = window.Headers;
		FormDataFunc = window.FormData;
	} else {
		// TODO: fix this to work in docker
		// const fetch = await import('node-fetch');
		// const { Headers } = await import('node-fetch');
		// const FormData = await import('form-data');
		// fetchFunc = fetch.default;
		// // @ts-ignore
		// HeadersFunc = Headers;
		// // @ts-ignore
		// FormDataFunc = FormData.default;
	}
})();

/**
 * In-memory storage object used as a fallback when localStorage is not available.
 * This is particularly useful in non-browser environments or when localStorage access is restricted.
 * @type {Record.<string, string>}
 */
let inMemoryStorage = {};

const getLocalStorageItem = (/** @type {string} */ key) => {
	if (browser) {
		return localStorage.getItem(key);
	} else {
		return inMemoryStorage[key] || null;
	}
};

class ApiService {
	static BASE_URL = '';
	/** @type {{ cookies: import('@sveltejs/kit').Cookies, url: URL } | null} */
	static serverSideContext = null;
	static deviceIdUpdateTracked = false;

	/**
	 * Sends a request to the API endpoint.
	 * @param {string} url - url path
	 * @param {object} payload - The payload to send with the request.
	 * @param {Function} fetchFunction - The fetch function to use (dependency injection for testing/non-browser env).
	 * @param {Function | null} [packFunction] - Optional pack function to use.
	 * @returns {Promise<any>} - A promise that resolves to the response from the API.
	 */
	static async request(url, payload, fetchFunction = fetchFunc, packFunction = null) {
		url = ApiService.BASE_URL + url;

		Sentry.setTag('api_url', url);

		if (!packFunction) {
			packFunction = browser ? ApiService.pack : ApiService.packSSR;
		}

		const packedPayload = packFunction(payload);

		if (!browser) {
			console.log(`SSR_Request to ${url}`);
		}

		const requestPromise = await fetchFunction(url, packedPayload);
		return ApiService.unpack(requestPromise);
	}

	/**
	 * Sends a request to the API endpoint.
	 * @param {string} url - url path
	 * @param {object} payload - The payload to send with the request.
	 * @param {object} args - The arguments to send with the request.
	 * @param {Function} fetchFunction - The fetch function to use (dependency injection for testing/non-browser env).
	 * @returns {Promise<any>} - A promise that resolves to the response from the API.
	 */
	static async requestAbcServiceLayer(url, payload, args = {}, fetchFunction = fetchFunc) {
		url = PAGES.EXTERNAL.ABC_SERVICE_LAYER_BASE_URL + url;

		Sentry.setTag('api_url', url);

		if (!browser) {
			return;
		}
		/// TODO: Get backend endpoint to fetch app version from server and
		/// store it, instead of getting it from the store where it might not
		/// be available. At the moment, we populate it during auth policy validation
		/// for some views.
		const appVersion = get(app)?.meta?.appVersion;

		if (!appVersion) {
			return;
		}

		const packedPayload = ApiService.packAbcServiceLayer(payload, appVersion, args);

		if (!packedPayload) {
			return;
		}

		const requestPromise = await fetchFunction(url, packedPayload);
		return ApiService.unpack(requestPromise);
	}

	/**
	 * Packs the payload into a format suitable for making a POST request.
	 * @param {object} payload - The payload to be packed.
	 * @param {string} appVersion - The app version to be packed.
	 * @returns {object | undefined} - The packed request object.
	 * @param {object} args - The arguments to send with the request.
	 */
	static packAbcServiceLayer(payload, appVersion, args = {}) {
		const headers = new HeadersFunc();
		const body = new FormDataFunc();

		if (payload) {
			Object.entries(payload).forEach(([name, value]) => {
				body.append(name, JSON.stringify(value));
			});
		}
		body.append('app_version', appVersion);
		body.append('device_id', getDeviceId() ?? '');
		body.append('arguments', JSON.stringify(args));

		// Attach client context indicating if this is a web environment (not in-app)
		let isWeb = true;
		try {
			// If running in the browser, detect in-app and invert
			isWeb = browser ? get(app)?.meta?.inApp === false : true;
		} catch (e) {
			// Fallback to true (web) if store access fails
			isWeb = true;
		}
		body.append('client_context', JSON.stringify({ is_web: isWeb }));

		const authToken = get(user).token;
		if (authToken !== null) {
			headers.append('Authorization', 'Bearer ' + authToken?.replace(/['"]+/g, ''));
		}

		headers.append('Accept', '*/*');

		Sentry.addBreadcrumb({
			category: 'api',
			message: 'API Pre-Request Payload',
			level: 'info',
			data: {
				payload
			}
		});

		return {
			method: 'POST',
			headers,
			body
		};
	}

	/**
	 * Packs the payload into a format suitable for making a POST request.
	 * @param {object} payload - The payload to be packed.
	 * @param {object} [options] - Options for packing
	 * @param {boolean} [options.preserveSessionDeviceId] - Whether to preserve the session device id (default: true)
	 * @returns {object} - The packed request object.
	 * TODO: Clean up preserveSessionDeviceId option implementation
	 */
	static pack(payload, options = {}) {
		const deviceId = ApiService.retrieveDeviceId();
		const clientContext = ApiService.getClientContext({
			preserveSessionDeviceId:
				options.preserveSessionDeviceId !== undefined ? options.preserveSessionDeviceId : true
		});
		const headers = new HeadersFunc();
		const body = new FormDataFunc();

		if (payload) body.append('arguments', JSON.stringify(payload));
		body.append('platform', PLATFORMS.DEFAULT);
		body.append('device_id', deviceId);
		body.append('aofl_product_uuid', ProductExperienceService.AOFL_PRODUCT_UUID);
		body.append('client_context', JSON.stringify(clientContext));

		const aoflToken = getLocalStorageItem(LOCAL_STORAGE_KEYS.AOFL_TOKEN);
		if (aoflToken !== null) {
			headers.append('Aofl-Token', aoflToken?.replace(/['"]+/g, ''));
		}

		headers.append('Accept', 'application/json');

		Sentry.addBreadcrumb({
			category: 'api',
			message: 'API Pre-Request Payload',
			level: 'info',
			data: {
				payload
			}
		});

		return {
			method: 'POST',
			headers,
			body
		};
	}

	/**
	 * Packs the payload into a format suitable for making a POST request.
	 * @param {object} payload - The payload to be packed.
	 * @returns {object} - The packed request object.
	 */
	static packSSR(payload) {
		const deviceId =
			payload?.device_id ??
			ApiService.serverSideContext?.cookies?.get(COOKIES.DEVICE_ID) ??
			'client_failure:packSSR:ssr_failure';

		const bodyFactory = this.createFormData();
		bodyFactory.append('arguments', JSON.stringify(payload), {});
		bodyFactory.append('device_id', deviceId);
		const body = bodyFactory.getBody();

		const headers = bodyFactory.getHeaders();

		return {
			method: 'POST',
			headers,
			body,
			cache: 'no-cache',
			credentials: 'include'
			// mode: 'cors'
		};
	}

	/**
	 * Retrieves the device ID from the store or the API.
	 * @returns {string} - The device ID.
	 */
	static retrieveDeviceId() {
		let deviceId = getDeviceId();

		if (deviceId) {
			return deviceId;
		}

		if (!deviceId) {
			// fallback to device ID from app.meta or fixed string
			deviceId = get(app)?.meta?.deviceId ?? 'client_failure:retrieveDeviceId:csr_failure';
		}

		return deviceId;
	}

	/**
	 * Gets the client context.
	 * @param {object} options - Options
	 * @static
	 * @returns {object} - The client context.
	 */
	static getClientContext(options = { preserveSessionDeviceId: true }) {
		let isWeb = true;
		let preserveSessionDeviceId = options.preserveSessionDeviceId;
		try {
			// If running in the browser, detect in-app and invert
			isWeb = browser ? get(app)?.meta?.inApp === false : true;
		} catch (e) {
			// Fallback to true (web) if store access fails
			isWeb = true;
		}

		let isAutomatedTesting = false;
		try {
			isAutomatedTesting = browser ? get(app)?.meta?.isAutomatedTesting === true : false;
		} catch (e) {
			// Fallback to false (not automated testing) if store access fails
			isAutomatedTesting = false;
		}

		const clientContext = {
			is_web: isWeb,
			is_automated_testing: isAutomatedTesting,
			preserve_session_device_id: preserveSessionDeviceId
		};

		try {
			const viewerCountry = get(app)?.meta?.viewerCountry;
			const viewerCountryRegion = get(app)?.meta?.viewerCountryRegion;
			// @ts-ignore - dynamically adding optional properties
			if (viewerCountry) clientContext.viewer_country = viewerCountry;
			// @ts-ignore - dynamically adding optional properties
			if (viewerCountryRegion) clientContext.viewer_country_region = viewerCountryRegion;
		} catch (e) {
			// If we can't access the store, just use the basic context
		}
		return clientContext;
	}

	/**
	 * Packs the payload into a format suitable for making a POST request.
	 * Does not rely on any context from the store or the API.
	 * @param {object} payload - The payload to be packed.
	 * @param {object} options - The options for the pack function.
	 * @param {?string} options.deviceId - The device id to pack into form data.
	 * @param {boolean} options.preserveSessionDeviceId - Whether to preserve the session device id.
	 * @returns {object} - The packed request object.
	 */
	static statelessPack(
		payload,
		options = { deviceId: 'fallback_device_id', preserveSessionDeviceId: true }
	) {
		const { deviceId } = options;
		const bodyFactory = this.createFormData();
		bodyFactory.append('arguments', JSON.stringify(payload), {});
		bodyFactory.append('device_id', deviceId);
		bodyFactory.append('platform', PLATFORMS.DEFAULT);
		bodyFactory.append(
			'client_context',
			JSON.stringify(
				ApiService.getClientContext({
					preserveSessionDeviceId: options.preserveSessionDeviceId
				})
			)
		);
		bodyFactory.append('aofl_product_uuid', ProductExperienceService.AOFL_PRODUCT_UUID);
		const body = bodyFactory.getBody();

		const headers = bodyFactory.getHeaders();

		return {
			method: 'POST',
			headers,
			body,
			cache: 'no-cache',
			credentials: 'include'
			// mode: 'cors'
		};
	}

	/**
	 * Unpacks the response from an API call and handles any error conditions.
	 * @param {Response} response - The response object from the API call.
	 * @returns {Promise<object>} - The payload of the response if successful.
	 * @throws {object} - An error object containing the error code, message, and payload if the API call was not successful.
	 */
	static async unpack(response) {
		const json = await response.json();
		const responseDeviceId = response.headers.get('device-id');
		const clientDeviceId = this.retrieveDeviceId();
		if (responseDeviceId && responseDeviceId !== clientDeviceId && validateUuid(responseDeviceId)) {
			saveDeviceId(responseDeviceId);
			if (!ApiService.deviceIdUpdateTracked) {
				ApiService.deviceIdUpdateTracked = true;
				// TODO: commenting out for now AD
				// AnalyticsService.statelessEventLog(
				// 	{
				// 		event_subtype: 'device_id_update',
				// 		client_device_id: clientDeviceId,
				// 		response_device_id: responseDeviceId
				// 	},
				// 	responseDeviceId
				// );
			}
		}

		if (response.status === 403 && typeof json.payload === 'undefined') {
			json.captcha = true;
			try {
				Sentry.addBreadcrumb({
					category: 'px-captcha-show',
					level: 'info'
				});

				CaptchaService.parseCaptchaResponse(json);
			} catch (err) {
				Sentry.captureException('API Service parseCaptchaResponse error', {
					extra: {
						errors: err
					}
				});
			}
		}

		let err = null;

		if (json.success !== 'TRUE') {
			const code = json.payload?.error?.code || 1;

			Sentry.setTag('error_code', code);
			const message = json.payload?.error?.message || 'unknown error.';
			const payload = json.payload?.error?.payload || {};
			err = new APIError(message, code, { payload });
		}

		if (err) {
			Sentry.addBreadcrumb({
				category: 'api',
				message: 'API Post-Request Error',
				level: 'error',
				data: {
					err
				}
			});
			throw err;
		}

		Sentry.addBreadcrumb({
			category: 'api',
			message: 'API Post-Request Success',
			level: 'info',
			data: {
				...json.payload
			}
		});

		return json.payload;
	}

	static createFormData() {
		const fields = {};
		const boundary = `------------------------${new Date().getTime().toString(16)}`;

		return {
			append: function (key, value, options) {
				options = options || {};
				fields[key] = {
					value: value,
					options: options
				};
			},

			getHeaders: function () {
				const headers = {
					'Content-Type': `multipart/form-data; boundary=${boundary}`
				};
				return headers;
			},

			getBody: function () {
				let body = '';
				for (const key in fields) {
					if (fields.hasOwnProperty(key)) {
						const field = fields[key];
						const name = key;
						const value = field.value;
						const options = field.options;

						body += `--${boundary}\r\n`;
						body += `Content-Disposition: form-data; name="${name}"`;

						if (options.filename) {
							body += `; filename="${options.filename}"\r\n`;
							body += `Content-Type: ${options.contentType || 'application/octet-stream'}\r\n\r\n`;
							body += `${value.toString('binary')}\r\n`;
						} else {
							body += '\r\n\r\n';
							body += `${value}\r\n`;
						}
					}
				}
				body += `--${boundary}--\r\n`;

				return body;
			}
		};
	}

	/**
	 * Fetches experiments from the API.
	 * @param {string} host - The host of the API.
	 * @param {object} payload - The payload for the getExperiments API call.
	 * @returns {Promise<{ results: object[] }>} A promise that resolves with the experiments.
	 */
	static getExperiments(host, payload) {
		const request = {
			aofl_product_uuid: ApiService.AOFL_PRODUCT_UUID,
			...payload
		};
		return ApiService.request(`${host}/ws/analytics/0.1/json/Experiment/Get/`, request);
	}

	/**
	 * Marks an experiment as seen.
	 * @param {string} host - The host of the API.
	 * @param {{ experiment_id: string; variant: string; key: string; profile: object; user: object; pageMetadata: object; }} payload - The payload for the markExperimentSeen API call.
	 * @returns {Promise<object>} A promise that resolves with the result of the markExperimentSeen request.
	 */
	static markExperimentSeen(host, { experiment_id, variant, key, profile, user, pageMetadata }) {
		const request = {
			aofl_product_uuid: ApiService.AOFL_PRODUCT_UUID,
			key_id: key,
			experiment_id,
			variant,
			profile,
			user,
			event_metadata: {
				page_metadata: pageMetadata
			}
		};
		return ApiService.request(`${host}/ws/analytics/0.1/json/Experiment/MarkSeen/`, request);
	}

	/**
	 * Fetches Paddle webhook data
	 * @param {{ lookup_name: string; lookup_value: string }} payload - The payload containing the lookup name and value.
	 * @returns {Promise<object>} A promise that resolves when the request is complete.
	 */
	static getPaddleWebhookEventInfo(payload) {
		const request = {
			lookup_name: payload?.lookup_name,
			lookup_value: payload?.lookup_value
		};
		return ApiService.request('/ws/amsl/0.1/json/Subscription/GetWebhookEventInfo/init', request);
	}

	/**
	 * Logs events to the server.
	 * @param {{ event: object }} events - The events to be logged.
	 * @param {Function} [fetchFunction] - Optional fetch function for dependency injection.
	 * @param {Function | null} [packFunction] - Optional pack function to use for payload.
	 * @returns {Promise<object>} A promise that resolves when the request is complete.
	 */
	static async eventLog({ event }, fetchFunction = fetchFunc, packFunction = null) {
		const request = {
			events: [event],
			...(event?.device_id ? { device_id: event.device_id } : {})
		};

		return await ApiService.request(
			'/ws/amsl/0.1/json/Event/Log/init',
			request,
			fetchFunction,
			packFunction
		);
	}

	/**
	 * Creates a device code for TV clients
	 * @returns {Promise<object>} - A promise that resolves with the response from the API.
	 */
	static createDeviceCode() {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID
		};

		return ApiService.request('/ws/amsl/0.1/json/DeviceCode/Create/init', request);
	}

	/**
	 * Checks if the given device code has an active session.
	 * @param {{ deviceCode: string; }} payload - The payload containing TV device code.
	 * @returns {Promise<object>} - A promise that resolves with the response from the API.
	 */
	static validateDeviceCodeSession(payload) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			device_code: payload?.deviceCode ?? ''
		};

		return ApiService.request('/ws/amsl/0.1/json/DeviceCode/VerifyDeviceSession/init', request);
	}

	/**
	 * Checks if a given device code is valid.
	 * @param {{ deviceCode: string; }} payload - The payload containing TV device code.
	 * @returns {Promise<object>} - A promise that resolves with the response from the API.
	 */
	static validateDeviceCode(payload) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			device_code: payload?.deviceCode ?? ''
		};

		return ApiService.request('/ws/amsl/0.1/json/DeviceCode/Validate/init', request);
	}

	/**
	 * Resolves pixel information for a given payload.
	 * @param {{ event: string; campaignInfo: object; }} payload - The payload containing event and campaign information.
	 * @returns {Promise<object>} - A promise that resolves with the response from the API.
	 */
	static resolvePixelInfo(payload) {
		const request = {
			product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			event: payload.event,
			campaign_info: payload.campaignInfo
		};

		return ApiService.request('/ws/amsl/0.1/json/Campaign/ResolvePixelInfo/init', request);
	}

	/**
	 * Performs payment pre-processing.
	 * @param {{ payment_provider: PAYMENT_TYPE; currency_code: string; payment_provider_info: object; }} payload - The payment provider.
	 * @returns {Promise<any>} A promise that resolves with the result of the payment pre-processing.
	 */
	static paymentPreProcess({ payment_provider, currency_code, payment_provider_info }) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			payment_provider,
			currency_code,
			payment_provider_info
		};
		return ApiService.request('/ws/amsl/0.1/json/Payment/PreProcess/init', request);
	}

	/**
	 * Performs a auxiliary product elibility check.
	 * @param {{ auxiliaryProductRole: string; userToProductUuid: string; }} payload - the role for which to check eligibility
	 * @returns {Promise<any>} A promise that resolves with the result of the eligibility check
	 */
	static getAuxiliaryProductEligiblity({ auxiliaryProductRole, userToProductUuid }) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			user_to_product_uuid: userToProductUuid,
			role: auxiliaryProductRole
		};
		return ApiService.request('/ws/amsl/0.1/json/Subscription/AuxiliaryEligibility/init', request);
	}

	/**
	 * Performs a cross product eligibility check
	 * @param {{ cross_product_aofl_product_uuid: string; }} payload - The payload to check eligibility
	 * @returns {Promise<any>} A promise that resolves with the result of the cross product purchase
	 */
	static crossProductCheckEligibility({ cross_product_aofl_product_uuid }) {
		const request = {
			cross_product_aofl_product_uuid: cross_product_aofl_product_uuid
		};
		return ApiService.request('/ws/amsl/0.1/json/CrossProduct/CheckEligibility/init', request);
	}

	/**
	 * Performs a cross product purchase
	 * @param {{ subscription_hash: string; cross_product_aofl_product_uuid: string; origin: string; password: string; }}  payload - The payload to purchase cross product.
	 * @returns {Promise<any>} A promise that resolves with the result of the cross product purchase.
	 */
	static crossProductSignUp({
		subscription_hash,
		cross_product_aofl_product_uuid,
		origin,
		password
	}) {
		const request = {
			cross_product_aofl_product_uuid,
			cross_product_hash: subscription_hash,
			origin,
			password
		};
		return ApiService.request('/ws/amsl/0.1/json/CrossProduct/SignUp/init', request);
	}

	/**
	 * Get product info.
	 * @param {object} payload payload
	 * @returns {Promise<object>}
	 */
	static getInfoForHash(payload) {
		const payloadWithProductHash = {
			...payload
		};
		return ApiService.request(
			'/ws/amsl/0.1/json/SubscriptionPlan/GetInfoForHash/init',
			payloadWithProductHash
		);
	}

	/**
	 * Get alternate subscription plan hash.
	 * @param {object} payload payload
	 * @returns {Promise<object>}
	 */
	static getAlternatePaymentSubscriptionPlanHash(payload) {
		const payloadWithProductHash = {
			...payload
		};
		return ApiService.request(
			'/ws/amsl/0.1/json/SubscriptionPlan/GetAlternatePaymentSubscriptionPlanHash/init',
			payloadWithProductHash
		);
	}

	/**
	 * Registers a prospect's email.
	 * @param {object} options - The options for email registration.
	 * @param {string} options.email_address - The email address to register.
	 * @param {string} options.prospect_type - The type of prospect.
	 * @param {string} options.language_locale - The language locale.
	 * @param {string} options.campaign_source_tag - The campaign source tag.
	 * @param {string} options.registration_type - The registration type.
	 * @param {string} options.subscription_status - The subscription status.
	 * @param {string} options.phone_number - The phone number to register.
	 * @returns {Promise<any>} A promise that resolves with the registration response.
	 */
	static prospectRegisterEmail({
		email_address,
		prospect_type,
		language_locale,
		campaign_source_tag,
		registration_type,
		subscription_status,
		phone_number
	}) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			email_address,
			prospect_type,
			language_locale,
			campaign_source_tag,
			registration_type,
			subscription_status
		};
		if (phone_number !== '') {
			request.phone_number = phone_number;
		}
		return ApiService.request('/ws/amsl/0.1/json/Prospect/RegisterEmail/init', request);
	}

	/**
	 * Retrieves the completed step using the provided parameters.
	 * @param {{completed_step: string;}} completed_step - The completed step object.
	 * @returns {Promise<object>} - A promise that resolves with the completed step data.
	 */
	static postCompletedStep({ completed_step }) {
		const request = {
			completed_step
		};
		return ApiService.request('/ws/amsl/0.1/json/RegistrationPath/GetCompletedStep/init', request);
	}

	/**
	 * // Possible soln. use a new key-step that is driven by reg-path config.
	 * Sends an order confirmation email to the user.
	 * @returns {Promise<object>} - A promise that resolves when the order confirmation request is completed.
	 */
	static regPathComplete() {
		const request = {};
		return this.request('/ws/amsl/0.1/json/RegistrationPath/Complete/init', request);
	}

	/**
	 * Retrieves subscription information for a subscriber.
	 * @param {object} [payload] - Optional payload object.
	 * @param {string} [payload.subscription_uuid] - Optional subscription UUID to target a specific subscription.
	 * @param {boolean} [payload.include_last_receipt_info] - Optional flag to include last receipt info.
	 * @returns {Promise<object>} A promise that resolves with the subscription information.
	 */
	static getSubscriptionInfo(payload) {
		const request = {};

		if (payload?.subscription_uuid) {
			request.subscription_uuid = payload.subscription_uuid;
		}
		if (payload?.include_last_receipt_info) {
			request.include_last_receipt_info = payload.include_last_receipt_info;
		}
		return ApiService.request('/ws/amsl/0.1/json/Subscriber/GetSubscriptionInfo/init', request);
	}

	/**
	 * Retrieves subscription information for a subscriber.
	 * @returns {Promise<{subscriptions: object[]}>} A promise that resolves with an object containing a "subscriptions" array.
	 */
	static getSubscriptions() {
		const request = {};
		return ApiService.request('/ws/amsl/0.1/json/Subscriber/GetSubscriptions/init', request);
	}

	/**
	 * Pre-processes a subscription for an existing user: reactivation or additional product purchase.
	 * @param {object} payload payload
	 * @returns {Promise<object>}
	 */
	static preProcessSubscription(payload) {
		const payloadWithProductId = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			...payload
		};
		return ApiService.request(
			'/ws/amsl/0.1/json/Subscription/PreProcessCreate/init',
			payloadWithProductId
		);
	}

	/**
	 * Pre-processes the subscription for a new user.
	 * @param {object} payload payload
	 * @returns {Promise<object>}
	 */
	static preProcessSubscriptionAndUser(payload) {
		const payloadWithProductId = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			...payload
		};
		return ApiService.request(
			'/ws/amsl/0.1/json/Subscription/PreProcessSignup/init',
			payloadWithProductId
		);
	}

	/**
	 * Logs a recipient email click.
	 * @param {object} payload payload
	 * @returns {Promise<object>}
	 */
	static receipientLogEmailClick(payload) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			...payload
		};
		return ApiService.request('/ws/amsl/0.1/json/Recipient/LogEmailClick/init', request);
	}

	/**
	 * Validates and gets meta data for a code.
	 * @param {object} payload payload
	 * @returns {Promise<object>}
	 */
	static validateCode(payload) {
		const payloadWithProductId = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			...payload
		};
		return ApiService.request('/ws/amsl/0.1/json/Item/Validate/init', payloadWithProductId);
	}

	/**
	 * Creates (signs-up) a user and creates a subscription.
	 * @param {object} payload payload
	 * @returns {Promise<object>}
	 */
	static createSubscriptionAndUser(payload) {
		return ApiService.request('/ws/amsl/0.1/json/Subscription/SignUp/init', payload);
	}

	/**
	 * Creates a subscription for an existing user: reactivation or additional product purchase.
	 * @param {object} payload payload
	 * @returns {Promise<object>}
	 */
	static createSubscription(payload) {
		return ApiService.request('/ws/amsl/0.1/json/Subscription/Create/init', payload);
	}

	/**
	 * Checks if a user is active.
	 * @param {object} options - The options for checking user activity.
	 * @param {string} options.username - The username.
	 * @param {string} options.password - The password.
	 * @param {string} options.country_abbrv - The country abbreviation.
	 * @param {string} options.username_type - The type of username.
	 * @returns {Promise<any>} A promise that resolves with the result of the user activity check.
	 */
	static userGetStatus(options) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			credential: options
		};
		return ApiService.request('/ws/amsl/0.1/json/User/GetStatus/init', request);
	}

	/**
	 * Checks if a user is legacy or shared services.
	 * @param {string} token - Session token.
	 * @returns {Promise<any>} A promise that resolves with the result of the user type check.
	 */
	static userGetInfo(token) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			token
		};
		return ApiService.request('/ws/amsl/0.1/json/User/GetInfo/init', request);
	}

	/**
	 * Checks if a user is legacy or shared services.
	 * @param {string} token - Session token.
	 * @returns {Promise<any>} A promise that resolves with the result of the user type check.
	 */
	static userGetType(token) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			token
		};
		return ApiService.request('/ws/amsl/0.1/json/User/GetType/init', request);
	}

	/**
	 * Upgrades the subscription plan.
	 * @param {object} options - The hash of the subscription plan.
	 * @param {string} options.subscription_plan_hash - The hash of the subscription plan.
	 * @param {string} options.action_source - The source of the action.
	 * @param {object} options.payment_provider_info - Payment info
	 * @returns {Promise<object>} - A promise that resolves with the result of the upgrade request.
	 */
	static subscriptionUpgrade({ subscription_plan_hash, action_source, payment_provider_info }) {
		const request = {
			subscription_plan_hash,
			action_source,
			payment_provider_info
		};
		return ApiService.request('/ws/amsl/0.1/json/Subscription/Upgrade/init', request);
	}

	/**
	 * Retrieves the upgrade eligibility information.
	 * @returns {Promise<object>} A promise that resolves with the upgrade eligibility information.
	 */
	static getUpgradeEligibility() {
		const request = {};
		return ApiService.request('/ws/amsl/0.1/json/Subscription/UpgradeEligibility/init', request);
	}

	/**
	 * Sends a request to initiate the password reset process for a user.
	 * @param {object} params - The forgot password parameters.
	 * @param {string} params.username_type - The type of username (e.g., email, username).
	 * @param {string} params.username - The username of the user.
	 * @param {string} [params.country_abbrv] - The abbreviation of the country (optional).
	 * @param {string} [params.aofl_product_uuid] - The aofl product uuid.
	 * @returns {Promise<object>} A promise that resolves with the response from the server.
	 */
	static userForgotPassword(params) {
		const request = {
			aofl_product_uuid: params.aofl_product_uuid || ProductExperienceService.AOFL_PRODUCT_UUID,
			username_type: params.username_type,
			username: params.username,
			country_abbrv: params.country_abbrv
		};
		return ApiService.request('/ws/amsl/0.1/json/User/ForgotPassword/init', request);
	}

	/**
	 * Logs in a user with the provided credentials.
	 * @param {object} credential - The credential object.
	 * @param {string} credential.username - The username.
	 * @param {string} credential.password - The password.
	 * @param {string} credential.country_abbrv - The country abbreviation.
	 * @param {string} credential.username_type - The type of username.
	 * @returns {Promise<any>} A promise that resolves with the login response.
	 */
	static login(credential) {
		const request = {
			credential,
			product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID
		};
		return ApiService.request('/ws/amsl/0.1/json/User/Login/init', request);
	}

	/**
	 * Logs out the user.
	 * @returns {Promise<object>} A promise that resolves when the user is successfully logged out.
	 */
	static userLogout() {
		const request = {};
		return ApiService.request('/ws/amsl/0.1/json/User/Logout/init', request);
	}

	/**
	 * Resets the user's password.
	 * @param {object} credentialInfo - The credential object.
	 * @param {string} credentialInfo.token - The reset password token.
	 * @param {string} credentialInfo.email - The user's email.
	 * @param {string} credentialInfo.password - The new password.
	 * @param {string} credentialInfo.countryAbbrv - The country abbreviation.
	 * @param {string} credentialInfo.usernameType - The type of username.
	 * @param {boolean} logOutOfDevices - Whether to log out of devices.
	 * @param {string} aoflProductUuid - The aofl product uuid.
	 * @returns {Promise<any>} A promise that resolves with the result of the password reset request.
	 */
	static userResetPassword(credentialInfo, logOutOfDevices, aoflProductUuid) {
		const request = {
			token: credentialInfo.token,
			aofl_product_uuid: aoflProductUuid || ProductExperienceService.AOFL_PRODUCT_UUID,
			credential: {
				username: credentialInfo.email,
				password: credentialInfo.password,
				country_abbrv: credentialInfo.countryAbbrv,
				username_type: credentialInfo.usernameType
			},
			log_out_of_devices: logOutOfDevices ?? false
		};
		return ApiService.request('/ws/amsl/0.1/json/User/ResetPassword/init', request);
	}

	/**
	 * Validates the login.
	 * @param {string} [token] - Optional authentication token
	 * @param {string} [product] - Optional product UUID
	 * @param {object} [options] - Options for the request
	 * @param {boolean} [options.preserveSessionDeviceId] - Whether to preserve the session device id (default: true)
	 * @returns {Promise<object>} A promise that resolves with the result of the login validation.
	 */
	static validateLogin(token, product, options = {}) {
		const request = {};

		if (token) {
			request.token = token;
		}

		if (product) {
			request.product = product;
		}

		// Create a wrapper pack function that uses the provided options
		const packFunctionWithOptions = (payload) => {
			return ApiService.pack(payload, options);
		};

		return ApiService.request(
			'/ws/amsl/0.1/json/User/ValidateLogin/init',
			request,
			fetchFunc,
			packFunctionWithOptions
		);
	}

	/**
	 * Creates session in legacy (used for ABCm product) for interchangeable sessions.
	 * @param {{ token: string; }} payload - payload for the call.
	 * @returns {Promise<object>} A promise that resolves with the result of the login validation.
	 */
	static createSessionsInLegacy(payload) {
		const token = payload.token;
		const request = [
			{
				credential: {
					token
				}
			}
		];
		return ApiService.request('/apis/mws/0.3/json/Account/Session/init', request);
	}

	/**
	 * Retrieves the subscription information from a third-party provider.
	 * @param {{platformType: PAYMENT_TYPE}} payload - The payment provider.
	 * @returns {Promise<object>} A promise that resolves with the plan information.
	 */
	static getThirdPartyProviderPlans({ platformType }) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			payment_provider: platformType
		};
		return ApiService.request('/ws/amsl/0.1/json/ThirdPartyProvider/GetPlans/init', request);
	}

	/**
	 * Logs a third-party conversion.
	 * @param {object} payload - The conversion data.
	 * @param {string} payload.conversion_event_id - The conversion event id.
	 * @param {string} payload.country_code - The country code.
	 * @param {object} payload.event_info - The event info.
	 * @param {Array<string>} payload.event_source - The event source.
	 * @param {string} payload.event_source_url - The event source url.
	 * @param {string} payload.event_type - The event type.
	 * @param {string} payload.hashed_email - The hashed email.
	 * @param {string} payload.user_agent - The user agent.
	 * @param {string} payload.user_to_product_uuid - The user to product uuid.
	 * @returns {Promise<object|any>}
	 */
	static logThirdPartyConversion({
		conversion_event_id,
		country_code,
		event_info,
		event_source,
		event_source_url,
		event_type,
		hashed_email,
		user_agent,
		user_to_product_uuid
	}) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			conversion_event_id,
			user_to_product_uuid,
			country_code,
			event_source,
			event_source_url,
			event_type,
			hashed_email,
			user_agent,
			event_info
		};
		return ApiService.request('/ws/amsl/0.1/json/ThirdPartyConversion/Log/init', request);
	}

	/**
	 * Retrieves the existing subscriptions for a third-party provider based on a receipt.
	 * @param {object} payload - The payload .
	 * @param {string} payload.payment_provider - The payment provider.
	 * @param {object} payload.payment_provider_info - The payment provider information.
	 * @returns {Promise<object|any>} A promise that resolves with the subscription information.
	 */
	static getThirdPartyProviderSubscriptions({ payment_provider, payment_provider_info }) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			payment_provider,
			payment_provider_info
		};
		return ApiService.request(
			'/ws/amsl/0.1/json/ThirdPartyProvider/GetSubscriptions/init',
			request
		);
	}

	/**
	 * Retrieves special offer template data.
	 * @param {string} offerHash - The URL slug that identifies the special offer
	 * @returns {Promise<object>} A promise that resolves with the offer template data.
	 */
	static getSpecialOfferTemplate(offerHash) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			url: offerHash
		};
		return ApiService.request('/ws/amsl/0.1/json/SpecialOffer/Get/init', request);
	}

	/**
	 * Sets the URL of the special offer that the user purchased in the user_meta_data table.
	 * @param {string} offerHash - The URL slug that identifies the special offer
	 * @returns {Promise<object>}
	 */
	static setSpecialOffer(offerHash) {
		const request = {
			special_offer: offerHash
		};
		return ApiService.request('/ws/amsl/0.1/json/RegistrationPath/SetSpecialOffer/init', request);
	}

	/**
	 * Terminates an Account (For Admin only).
	 * @param {'primary' | 'auxiliary'| null} category - The subscription plan category to terminate
	 * @returns {Promise<any>} A promise that resolves with the termination result.
	 */
	static terminateSubscriptionAdmin(category) {
		const request = {
			subscription_plan_category: category
		};
		return ApiService.request('/ws/amsl/0.1/json/Subscription/Terminate/init', request);
	}
	/**
	 * Retrieves the existing subscription for a third-party provider based on a receipt.
	 * @param {object} payload - The payload .
	 * @param {string} payload.payment_provider - The payment provider.
	 * @param {object} payload.payment_provider_info - The payment provider information.
	 * @returns {Promise<object>} A promise that resolves with the subscription information.
	 */
	static getThirdPartyProviderSubscription({ payment_provider, payment_provider_info }) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			payment_provider,
			payment_provider_info
		};
		return ApiService.request('/ws/amsl/0.1/json/ThirdPartyProvider/GetSubscription/init', request);
	}
	/**
	 * Process item purchase.
	 * @param {object} payload payload
	 * @returns {Promise<object>}
	 */
	static itemPurchase(payload) {
		const payloadWithProductId = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			...payload
		};
		return ApiService.request('/ws/amsl/0.1/json/Item/Purchase/init', payloadWithProductId);
	}

	/**
	 * Updates email unsubscribe preferences for user.
	 * @param {object} payload payload
	 * @param {string} payload.email_address - The user's email address.
	 * @param {string} payload.subscription_status - The new subscription status.
	 * @returns {Promise<object>}
	 */
	static receipientUpdateStatus({ email_address, subscription_status }) {
		const payload = {
			email_address,
			subscription_status
		};
		return ApiService.request('/ws/amsl/0.1/json/Recipient/UpdateStatus/init', payload);
	}
	/**
	 *
	 * Exchanges a short-life token for a new token
	 * @static
	 * @param {object} payload - The payload object.
	 * @param {string} payload.token - The token.
	 * @param {string} payload.product_uuid - The product uuid.
	 * @returns {Promise<any>}
	 * @memberof ApiService
	 */
	static tokenIntrospect({ token, product_uuid }) {
		const request = {
			token,
			product_uuid: product_uuid || ProductExperienceService.AOFL_PRODUCT_UUID
		};
		return ApiService.request('/ws/amsl/0.1/json/Token/Introspect/init', request);
	}

	/**
	 *
	 * Creates a short-lived token
	 * @static
	 * @param {string} productUuid - The product uuid to create the token for. If not provided, the default product uuid will be used.
	 * @returns {Promise<any>}
	 * @memberof ApiService
	 */
	static tokenCreate(productUuid) {
		const request = {
			product_uuid: productUuid || ProductExperienceService.AOFL_PRODUCT_UUID
		};
		return ApiService.request('/ws/amsl/0.1/json/Token/Create/init', request);
	}

	/**
	 * Gets the latest payment information for the current user.
	 * @returns {Promise<object>} A promise that resolves with the user's payment information.
	 */
	static getPaymentInfo() {
		const request = {};
		return ApiService.request('/ws/amsl/0.1/json/User/GetPaymentInfo/init', request);
	}

	/**
	 * Logs in a user with the provided credentials.
	 * @param {object} credential - The credential object.
	 * @param {string} credential.username - The username.
	 * @param {string} credential.password - The password.
	 * @param {string} credential.country_abbrv - The country abbreviation.
	 * @param {string} credential.username_type - The type of username.
	 * @returns {Promise<any>} A promise that resolves with the login response.
	 */
	static async legacyTransfer(credential) {
		const request = {
			credential,
			product_uuid: AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0
		};
		return ApiService.request('/ws/amsl/0.1/json/User/LegacyTransfer/init', request);
	}

	/**
	 * @description Retrieves a short-lived token for a legacy transfer user
	 * @param {string} token - The token to use for the legacy transfer login
	 * @returns {Promise<any>} A promise that resolves with the login response
	 */
	static async getTransferUserShortLivedToken(token) {
		const request = {
			token,
			product_uuid: AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0
		};
		const shortLivedTokenResponse = await ApiService.request(
			'/ws/amsl/0.1/json/User/GetTransferUserShortLivedToken/init',
			request
		);
		return shortLivedTokenResponse?.short_lived_token ?? '';
	}

	/**
	 * Cancels a subscription (opt-out).
	 * @param {object} payload - The cancellation payload.
	 * @param {string=} payload.subscription_uuid - Optional subscription UUID to target a specific subscription.
	 * @param {string} payload.cancellation_source - Source of the cancellation action.
	 * @param {string=} payload.cancellation_reason - Optional free-form reason for cancellation.
	 * @returns {Promise<object>} A promise that resolves with the result of the cancellation.
	 */
	static subscriptionCancel({ subscription_uuid, cancellation_source, cancellation_reason }) {
		/** @type {Record<string, any>} */
		const request = {
			cancellation_source
		};
		if (subscription_uuid) request.subscription_uuid = subscription_uuid;
		if (cancellation_reason) request.cancellation_reason = cancellation_reason;
		return ApiService.request('/ws/amsl/0.1/json/Subscription/OptOut/init', request);
	}

	/**
	 * Pauses subscription for a subscriber.
	 * @param {object} payload - The pause payload.
	 * @param {string} payload.term - specific pause term(weekly for now).
	 * @param {string} payload.period - The period of the pause(3, 6 and 12 months).
	 * @returns {Promise<boolean>} A promise that resolves with the pause result if no error true if error false.
	 */
	static subscriptionPause({ term, period }) {
		const request = {
			term,
			period
		};
		return ApiService.request('/ws/amsl/0.1/json/Pause/AddToQueue/init', request);
	}

	/**
	 * Resumes a subscription.
	 * @returns {Promise<boolean>} A promise that resolves with the resume result if no error true if error false.
	 */
	static subscriptionResumeSubcriptionFromPause() {
		return ApiService.request(
			'/ws/amsl/0.1/json/Pause/ResumeFromPause/init',
			{} /** empty payload */
		);
	}

	/**
	 * Checks if user is eligible for pause subscription.
	 * @returns {Promise<{ is_eligible: boolean }>} A promise that resolves with the pause result if no error true if error false.
	 */
	static isSubscriptionPauseEligible() {
		return ApiService.request('/ws/amsl/0.1/json/Pause/IsEligible/init', {} /** empty payload */);
	}

	/**
	 * Updates user credentials (email and/or password).
	 * @param {object} credentials - The credential update object.
	 * @param {string} credentials.token - The session token.
	 * @param {string} [credentials.username] - The new email address (optional).
	 * @param {string} [credentials.new_password] - The new password (optional).
	 * @param {string} [credentials.old_password] - The current password (required if updating password).
	 * @param {string} [credentials.product_uuid] - The product uuid.
	 * @returns {Promise<object>} A promise that resolves with the updated user information.
	 */
	static updateUserCredentials(credentials) {
		const request = {
			token: credentials.token,
			aofl_product_uuid: credentials.product_uuid || ApiService.AOFL_PRODUCT_UUID,
			username: credentials.username || '',
			new_password: credentials.new_password || '',
			old_password: credentials.old_password || ''
		};

		return ApiService.request('/ws/amsl/0.1/json/User/UpdateCredentials/init', request);
	}

	/**
	 * Retrieves all receipts for a subscriber.
	 * @returns {Promise<object>} A promise that resolves with the subscription information.
	 */
	static getSubscriptionReceipts() {
		const request = {};
		return ApiService.request('/ws/amsl/0.1/json/Subscriber/GetReceipts/init', request);
	}

	/**
	 * Converts a JWT to short lived token
	 * @param {string} token - The token to convert.
	 * @param {string} productUuid - Product uuid to use for the request.
	 * @returns {Promise<{short_lived_token: string}>} A promise that resolves with the short lived token.
	 */
	static getAbcmouseToken(token, productUuid) {
		const request = { token, product_uuid: productUuid || AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0 };
		return ApiService.request('/ws/amsl/0.1/json/User/GetAbcmouseToken/init', request);
	}

	/**
	 * Validates a recovery token.
	 * @param {string} token - The token to validate.
	 * @returns {Promise<object>} A promise that resolves with the result of the validation.
	 */
	static userValidateRecoveryToken(token) {
		const request = {
			token,
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID
		};
		return ApiService.request('/ws/amsl/0.1/json/User/ValidateRecoveryToken/init', request);
	}

	/**
	 * Retrieves the recipient email info.
	 * @param {object} payload - The payload.
	 * @param {string} payload.email_address - The email to retrieve the info for.
	 * @returns {Promise<{ email_address: string }>} A promise that resolves with the recipient email info.
	 */
	static recipientGetEmailInfo({ email_address }) {
		const request = {
			email_address,
			product_name: ProductExperienceService.AOFL_PRODUCT_NAME,
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID
		};
		return ApiService.request('/ws/amsl/0.1/json/Recipient/GetEmailInfo/init', request);
	}

	/**
	 * Updates subscription payment info.
	 * @param {{
	 *   payment_provider: string,
	 *   payment_provider_info: object,
	 * }} payload - The update payload
	 * @returns {Promise<object>} A promise that resolves with the result of the update request.
	 */
	static subscriptionUpdatePaymentInfo({ payment_provider, payment_provider_info }) {
		const request = {
			payment_provider,
			payment_provider_info
		};
		return ApiService.request('/ws/amsl/0.1/json/Subscriber/ChangePayment/init', request);
	}

	/**
	 * Adds credentials to account.
	 * @param {{
	 *   username: string,
	 *   password: string,
	 *   countryAbbrv: string,
	 *   usernameType: string
	 * }} credentials - The credentials to add
	 * @param {string} token - User auth token
	 * @param {string} aoflProductUuid - The aofl product uuid.
	 * @returns {Promise<*>}
	 */
	static addCredentials(credentials, token, aoflProductUuid) {
		const request = {
			credential: {
				username: credentials.username,
				password: credentials.password,
				country_abbrv: credentials.countryAbbrv,
				username_type: credentials.usernameType
			},
			token,
			aofl_product_uuid: aoflProductUuid
		};
		return ApiService.request('/ws/amsl/0.1/json/Account/AddCredentials/init', request);
	}

	/**
	 * Creates a device session for device-based authentication.
	 * No authentication required - establishes initial authentication for devices.
	 * @param {string} deviceId - The device ID to use for the device session.
	 * @returns {Promise<object>} A promise that resolves with the device session info containing token and user_to_product_uuid.
	 */
	static createDeviceSession(deviceId) {
		const request = {
			product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			device_id: deviceId
		};
		return ApiService.request('/ws/amsl/0.1/json/User/CreateDeviceSession/init', request);
	}

	/**
	 * Retrieves all receipts for a subscriber by subscription UUID.
	 * @param {string} subscriptionUuid - The subscription UUID.
	 * @returns {Promise<object>} A promise that resolves with the subscription information.
	 */
	static getReceiptsBySubscriptionUuid(subscriptionUuid) {
		const request = {
			subscription_uuid: subscriptionUuid
		};
		return ApiService.request(
			'/ws/amsl/0.1/json/Subscriber/GetReceiptsBySubscriptionUuid/init',
			request
		);
	}

	/**
	 * Posts analytics identifiers for the current subscriber.
	 * Called after successful subscribe/resubscribe. Uses defaults that
	 * append new identifiers and prefer newer values on conflicts.
	 * @param {object} payload - The payload containing analytics identifiers and configuration.
	 * @param {string} payload.user_to_product_uuid - Target user-to-product UUID.
	 * @param {{
	 *   adid?: string|null,
	 *   idfa?: string|null,
	 *   idfv?: string|null,
	 *   gps_adid?: string|null,
	 *   android_id?: string|null,
	 *   app_instance_id?: string|null
	 * }} payload.identifiers - Identifier values (nullable).
	 * @param {'merge'} [payload.mode] - Merge strategy.
	 * @param {'new_precedence'|'existing_precedence'} [payload.precedence] - Conflict precedence.
	 * @returns {Promise<object>}
	 */
	static subscriberSetAnalyticsIdentifiers({
		user_to_product_uuid,
		identifiers,
		mode = 'merge',
		precedence = 'new_precedence'
	}) {
		const request = {
			aofl_product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
			user_to_product_uuid,
			identifiers,
			mode,
			precedence
		};
		return ApiService.request('/ws/amsl/0.1/json/Subscriber/SetAnalyticsIdentifiers/init', request);
	}

	/**
	 * Checks if the user is eligible for one click cancellation.
	 * @param {object} payload - The payload.
	 * @param {string} payload.subscriptionUuid - Optional subscription uuid.
	 * @returns {Promise<{ is_eligible: boolean }>} A promise that resolves with the eligibility information.
	 */
	static isOneClickCancellationEligible(payload) {
		const request = {};

		if (payload?.subscriptionUuid) {
			request.subscription_uuid = payload?.subscriptionUuid;
		}

		return ApiService.request(
			'/ws/amsl/0.1/json/Subscriber/IsOneClickCancellationEligible/init',
			request
		);
	}

	/**
	 * Checks if the user has subscribed to a plan.
	 * @param {string} productUuid - The product uuid.
	 * @param {string} planHash - The plan hash.
	 * @returns {Promise<{ has_subscribed: boolean }>} A promise that resolves with the eligibility information.
	 */
	static hasSubscribed(productUuid, planHash) {
		const request = {
			aofl_product_uuid: productUuid,
			subscription_plan_hash: planHash
		};
		return ApiService.request('/ws/amsl/0.1/json/User/HasSubscribed/init', request);
	}

	/**
	 * Authenticates a password.
	 * @param {string} password - The password to authenticate.
	 * @returns {Promise<{ is_authenticated: boolean }>} A promise that resolves with the authentication information.
	 */
	static authenticatePassword(password) {
		const request = {
			password: password
		};
		return ApiService.request('/ws/amsl/0.1/json/User/AuthenticatePassword/init', request);
	}

	/**
	 * Upgrades a subscription.
	 * @param {string} subscriptionPlanHash - The plan hash.
	 * @param {string} actionSource - The source of the action.
	 * @returns {Promise<object>} A promise that resolves with the upgrade information.
	 */
	static upgradeRequest(subscriptionPlanHash, actionSource) {
		const request = {
			subscription_plan_hash: subscriptionPlanHash,
			action_source: actionSource
		};
		return ApiService.request('/ws/amsl/0.1/json/Subscription/Upgrade/init', request);
	}
	/**
	 * Retrieves printables info.
	 * @param {number} page = 1
	 * @param {number} itemsPerPage = 10
	 * @returns {Promise<object>} A promise that resolves with the result of the update request.
	 */
	static getPrintablesInfo(page = 1, itemsPerPage = 10) {
		return ApiService.requestAbcServiceLayer(
			'/ws/mws/1.0/json/Printables/Get/',
			{},
			{
				page,
				limit: itemsPerPage
			}
		);
	}

	/**
	 * Retrieves printables info.
	 * @param {number} page = 1
	 * @param {number} itemsPerPage = 10
	 * @returns {Promise<object>} A promise that resolves with the result of the update request.
	 */
	static getPrintablesFeatureActivities(page = 1, itemsPerPage = 10) {
		return ApiService.requestAbcServiceLayer(
			'/ws/mws/1.0/json/Printables/GetFeatured/',
			{},
			{
				page,
				limit: itemsPerPage
			}
		);
	}

	/**
	 * Retrieves printables info.
	 * @returns {Promise<object>} A promise that resolves with the result of the update request.
	 */
	static getPrintablesFilters() {
		return ApiService.requestAbcServiceLayer('/ws/mws/1.0/json/Printables/GetMetaInfo/', {});
	}
}
export { ApiService };
